import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type SnapFloodFormValues = {
  lenderId: string;
};

interface EditSnapFloodIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditSnapFloodIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditSnapFloodIntegrationSettingsProps): JSX.Element => {
  // Determine if we're in edit mode
  const isEditMode = !!integrationSettings;

  let lenderId = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    lenderId = data.lenderId || '';
  }

  const validationSchema = yup.object().shape({
    lenderId: yup.string().required('Lender ID is required'),
  });

  const { control, handleSubmit, formState } = useForm<SnapFloodFormValues>({
    defaultValues: {
      lenderId: lenderId,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: SnapFloodFormValues) => {
    console.log('Form validated successfully:', data);

    // Create or update IntegrationSettings object when form is valid
    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        // Update existing settings
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            lenderId: data.lenderId,
          }),
        };
      } else {
        // Create new settings
        settings = {
          name: 'Snap Flood Integration', // This will be overridden by parent
          vendor: IntegrationVendor.Snap,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            lenderId: data.lenderId,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Snap Flood Settings' : 'Snap Flood Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="lenderId"
            label="Lender ID *"
            placeholder="Enter your Snap Flood Lender ID"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
